import { useState, useMemo } from 'react';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';
import {
  useUpdateStepGoalMutation,
  useUpdateTaskMutation,
  useCompleteTaskMutation,
} from 'services/checklistService';

export enum StepDetailsDialogState {
  NONE,
  LOADING,
  GOAL_EDIT,
  GOAL_SAVED,
  STEPS_EDIT,
  STEPS_SAVED,
  ERROR,
}

export const useStepDetails = (task: PhysicalActivityTask) => {
  const { trackEvent } = usePendo();

  const [dialogState, setDialogState] = useState(StepDetailsDialogState.NONE);

  // State of the edit step goal input field
  const [stepGoalInput, setStepGoalInput] = useState(task.stepGoal);
  const [updateStepGoal] = useUpdateStepGoalMutation();

  // State of the edit step count input field
  const [stepCountInput, setStepCountInput] = useState(task.recordedSteps ?? 0);
  const [updateStepTask] = useUpdateTaskMutation();
  const [completeStepTask] = useCompleteTaskMutation();

  const handleStepTaskRecordedStepsChange = async () => {
    const hasReachedStepGoal = stepCountInput >= task.stepGoal;
    const taskId = task.id;

    const stepCountBodyData: PhysicalActivityTaskBodyData = {
      sent_back_data: { passive_steps: stepCountInput },
    };

    // If step goal is reached, complete the task. Otherwise, just update it.
    if (!task.isCompleted && hasReachedStepGoal) {
      const completeStepTaskResponse = await completeStepTask({
        taskId,
        body: stepCountBodyData,
      });
      if (!completeStepTaskResponse.error) {
        trackEvent(PendoEventType.CHECKLIST_TASK_COMPLETED, {
          eventType: PendoEventType.CHECKLIST_TASK_COMPLETED,
          taskId: `${taskId}`,
        });
      }
      return completeStepTaskResponse;
    }

    return updateStepTask({ taskId, body: { task: stepCountBodyData } });
  };

  const handleDialogYes = async () => {
    switch (dialogState) {
      case StepDetailsDialogState.GOAL_EDIT: {
        setDialogState(StepDetailsDialogState.LOADING);
        const { error } = await updateStepGoal(stepGoalInput.toString());

        const newDialogState = error
          ? StepDetailsDialogState.ERROR
          : StepDetailsDialogState.GOAL_SAVED;
        setDialogState(newDialogState);
        break;
      }
      case StepDetailsDialogState.STEPS_EDIT: {
        setDialogState(StepDetailsDialogState.LOADING);

        const { error } = await handleStepTaskRecordedStepsChange();

        const newDialogState = error
          ? StepDetailsDialogState.ERROR
          : StepDetailsDialogState.STEPS_SAVED;
        setDialogState(newDialogState);
        break;
      }
      default: {
        setDialogState(StepDetailsDialogState.NONE);
        break;
      }
    }
  };

  const handleDialogNo = () => {
    switch (dialogState) {
      case StepDetailsDialogState.GOAL_EDIT:
        setDialogState(StepDetailsDialogState.NONE);
        break;
      case StepDetailsDialogState.STEPS_EDIT:
        setDialogState(StepDetailsDialogState.NONE);
        break;
      case StepDetailsDialogState.LOADING:
        // Prevent the user from closing the dialog while loading
        break;
      default:
        setDialogState(StepDetailsDialogState.NONE);
        break;
    }
  };

  const handleEditGoalClicked = () => {
    // Reset step goal value
    setStepGoalInput(task.stepGoal);
    setDialogState(StepDetailsDialogState.GOAL_EDIT);
  };

  const handleEditStepsClicked = () => {
    // Reset step count value
    setStepCountInput(task.recordedSteps ?? 0);
    setDialogState(StepDetailsDialogState.STEPS_EDIT);
  };

  const isDialogYesButtonEnabled = useMemo(() => {
    switch (dialogState) {
      case StepDetailsDialogState.STEPS_EDIT:
        if (task.isCompleted) {
          return stepCountInput > (task?.recordedSteps ?? 0);
        }

        return stepCountInput > 0;
      case StepDetailsDialogState.GOAL_EDIT:
        return stepGoalInput > 0 && stepGoalInput !== task.stepGoal;
      default:
        return true;
    }
  }, [dialogState, stepCountInput, stepGoalInput, task.isCompleted, task.stepGoal]);

  return {
    dialogState,
    stepCountInput,
    setStepCountInput,
    stepGoalInput,
    setStepGoalInput,
    handleDialogYes,
    isDialogYesButtonEnabled,
    handleDialogNo,
    handleEditGoalClicked,
    handleEditStepsClicked,
  };
};
